import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { FormulaNonFinancialDriver, FreestyleNonFinancialDriver } from '../../../core/api/types/non-financial-drivers';
import { InputMethod } from '../../../ui/data-builder/types';
import { NO_GROUP_VALUE } from '../../components/add-to-group-menu/add-to-group-menu';

export const useSharedDriverForm = (
  driver?: FreestyleNonFinancialDriver | FormulaNonFinancialDriver,
  groups?: { id: string; name: string }[]
) => {
  const intl = useIntl();

  const fieldsValidated = useRef(false);

  const [addToGroup, setAddToGroup] = useState('');
  const [decimalDisplay, setDecimalDisplay] = useState('0');
  const [errors, setErrors] = useState<WizardFormErrors>();
  const [inputMethod, setInputMethod] = useState(InputMethod.FREESTYLE);
  const [name, setName] = useState('');

  const getRequestData = useCallback(() => {
    return {
      name,
      driver_group: addToGroup,
      decimal_display: Number(decimalDisplay)
    };
  }, [name, addToGroup, decimalDisplay]);

  const handleAddToGroupChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newAddToGroupValue = event.target.value;
    if (newAddToGroupValue === NO_GROUP_VALUE) {
      setAddToGroup('');
      return;
    }
    setAddToGroup(event.target.value);
  }, []);

  const handleDecimalDisplayChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDecimalDisplay(event.target.value);
  }, []);

  const handleInputMethodChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputMethod(event.target.value as InputMethod);
  }, []);

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setName(event.target.value);
  }, []);

  const validate = useCallback(() => {
    fieldsValidated.current = true;
    const newErrors = {} as WizardFormErrors;

    // Name field
    if (!name) {
      newErrors.name = {
        id: 'name',
        message: intl.formatMessage({
          id: 'predict.non_financial_drivers.driver_name_required',
          defaultMessage: 'Driver name is required'
        })
      };
    }

    const isValid = Object.keys(newErrors).length === 0;
    setErrors(!isValid ? newErrors : undefined);
    return isValid;
  }, [name, intl]);

  const revalidate = useCallback(() => {
    if (fieldsValidated.current) {
      validate();
    }
  }, [validate]);

  // If the driver changes, update the name, input method, group and decimal display fields
  useEffect(() => {
    if (driver) {
      setName(driver.name);
      setAddToGroup(driver.driver_group || '');
      setInputMethod((driver.driver_type as unknown) as InputMethod);
      setDecimalDisplay(String(driver.decimal_display));
    } else {
      setName('');
      setAddToGroup('');
      setInputMethod(InputMethod.FREESTYLE);
      setDecimalDisplay('0');
    }
  }, [driver]);

  // If the groups change and the group we had selected no longer exists, clear data
  useEffect(() => {
    if (addToGroup && groups) {
      if (!groups.find(item => item.id === addToGroup)) {
        setAddToGroup('');
      }
    }
  }, [addToGroup, groups]);

  return {
    addToGroup,
    decimalDisplay,
    errors,
    getRequestData,
    inputMethod,
    name,
    handleAddToGroupChange,
    handleDecimalDisplayChange,
    handleInputMethodChange,
    handleNameChange,
    revalidate,
    validate
  };
};
